import React, { useEffect, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { AlertContent, AlertHeader, Button, Icon, Logo, ModalFactory, Text } from 'components';
import { CONNECT_OAUTH_KEY } from 'config/const';
import { lineHeight, theme, toRem } from 'design';
import useConnectIntegration from 'hooks/useConnectIntegration';
import { storage } from 'lib';
import { routeConstants } from 'routes';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { deleteConnectionRequest, getIntegrationConnectionsRequest, getOauthUrlRequest } from 'store/Collections/Integrations';
import * as ActionTypes from 'store/Collections/Integrations/types';
import { IStore } from 'store/types';
import { IConnection, IOauthURL, IOrganizationConnection } from 'types';
import { toTitleCase } from 'utils/strings';
import { Layout, LoadingHeader, StyledAuthHeader } from './styles';

const ContentContainer = styled.div`
  flex-flow: column;
  width: 100%;
  gap: ${toRem(theme.space.xl)};
`;

const ColumnDiv = styled.div`
  flex-flow: column;
`;

const ItemTitle = styled.div`
  gap: ${toRem(theme.space.sm)};
  align-items: center;
`;

const Connection = styled.div`
  background-color: ${theme.color.white};
  border: 1px solid ${theme.color.neutral[200]};
  margin-top: ${toRem(theme.space.sm)};
  padding: ${toRem(theme.space.lg)};
  justify-content: space-between;
`;

const NoConnection = styled.div`
  background-color: ${theme.color.white};
  border: 1px solid ${theme.color.neutral[200]};
  margin-top: ${toRem(theme.space.sm)};
  padding: ${toRem(theme.space.lg)};
  justify-content: center;
`;

const NoConnectionText = styled(Text)`
  color: ${theme.color.neutral[500]};
  font-style: italic;
  font-size: ${toRem(theme.font.size.md)};
  line-height: ${lineHeight(theme.font.size.md)};
`;

const ConnectionName = styled.div`
  gap: ${toRem(theme.space.sm)};
  align-items: center;

  p {
    font-size: ${toRem(theme.font.size.md)};
    line-height: ${lineHeight(theme.font.size.md)};
  }
`;

const ActionButtons = styled.div`
  flex-flow: column;
  justify-content: center;
  align-items: center;
  gap: ${toRem(theme.space.lg)};
`;

const DisconnectButton = styled(Button)`
  color: ${theme.color.danger[50]};

  &:hover,
  &:focus {
    color: ${theme.color.danger[50]};
  }
`;

const StyledLogo = styled(Logo)`
  width: ${toRem(16)};
  height: ${toRem(16)};
`;

const ModalContent = styled.div`
  justify-content: center;
  background-color: ${theme.color.neutral[50]};
  padding: ${toRem(theme.space.lg)};
  width: 100%;
`;

export const Connections = () => {
  const integration = useConnectIntegration();
  const integrationName = toTitleCase(integration?.name);
  const dispatch = useDispatch();
  const [organizationConnectionsList, setOrganizationConnectionsList] = useState<IOrganizationConnection[]>();
  const [connectionToDelete, setConnectionToDelete] = useState<{ orgId: string; connection: IConnection }>();
  const isLoadingDelete = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, ActionTypes.Integrations.DELETE_CONNECTION_REQUEST));
  const isLoadingOauthUrl = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, ActionTypes.Integrations.GET_OAUTH_URL_REQUEST));

  useEffect(() => {
    if (integration) {
      dispatch(getIntegrationConnectionsRequest(integration.name, (response) => setOrganizationConnectionsList(response)));
    }
  }, [integration, dispatch]);

  const handleConnect = () => {
    dispatch(
      getOauthUrlRequest(integration.name, (response: IOauthURL) => {
        storage.setItem(CONNECT_OAUTH_KEY, response.state);
        window.open(response.url, '_self');
      })
    );
  };

  const onDeleteSuccess = () => {
    const newConnectionsList: IOrganizationConnection[] = organizationConnectionsList.map((organizationConnection) => {
      if (organizationConnection.organization?.id === connectionToDelete.orgId) {
        const newConnections = organizationConnection.connections.filter((connection) => connection.id !== connectionToDelete.connection.id);
        return { organization: organizationConnection.organization, connections: newConnections };
      }
      return organizationConnection;
    });

    setOrganizationConnectionsList(newConnectionsList);
    hideDisconnectModal();
    setConnectionToDelete(undefined);
  };

  const [showDisconnectModal, hideDisconnectModal] = useModal(
    () => (
      <ModalFactory customModal onClose={handleModalClose} isActive>
        <AlertContent
          title="Are you sure you want to disconnect this account?"
          leftActionLabel="Cancel"
          rightActionLabel="Disconnect"
          onLeftAction={handleModalClose}
          onRightAction={() => {
            dispatch(deleteConnectionRequest(integration.name, connectionToDelete.connection.id, connectionToDelete.orgId, onDeleteSuccess));
          }}
          rightActionAppearance="destructive"
          isLoading={isLoadingDelete}
        >
          <ModalContent>
            <ConnectionName>
              {/* @ts-ignore */}
              <Icon width={16} height={16} icon={`${integrationName}Logo`} />
              <Text>{connectionToDelete.connection.provider?.name}</Text>
            </ConnectionName>
          </ModalContent>
        </AlertContent>
      </ModalFactory>
    ),
    [connectionToDelete, isLoadingDelete]
  );

  const handleModalClose = () => {
    hideDisconnectModal();
    setConnectionToDelete(undefined);
  };

  const handleDisconnect = (orgId: string, connection: IConnection) => {
    setConnectionToDelete({ orgId, connection });
    showDisconnectModal();
  };

  const renderContent = () => (
    <ContentContainer>
      {organizationConnectionsList?.map((organizationConnection) => (
        <ColumnDiv key={organizationConnection.organization.id} data-cy={organizationConnection.organization.id}>
          <ItemTitle>
            <StyledLogo />
            <Text fontWeight="semibold">{organizationConnection.organization?.name}</Text>
          </ItemTitle>
          {organizationConnection.connections?.length > 0 ? (
            organizationConnection.connections.map((connection) => (
              <Connection key={connection.id} data-cy={connection.id}>
                <ConnectionName>
                  {/* @ts-ignore */}
                  <Icon width={16} height={16} icon={`${integrationName}Logo`} />
                  <Text>{connection.provider?.name}</Text>
                </ConnectionName>
                <DisconnectButton onClick={() => handleDisconnect(organizationConnection.organization.id, connection)} label="Disconnect" appearance="link" />
              </Connection>
            ))
          ) : (
            <NoConnection>
              <NoConnectionText size="small">No merchant accounts connected</NoConnectionText>
            </NoConnection>
          )}
        </ColumnDiv>
      ))}
    </ContentContainer>
  );

  if (organizationConnectionsList?.length === 0) {
    return (
      <Layout>
        <ColumnDiv>
          <AlertHeader title={`Unable to link ${integrationName} Account`} />
          <Text size="small" fontWeight="medium">{`To connect ${toTitleCase(integration.name)} to a Kickfin account you must be an approved Organization Admin. For assistance contact support@kickfin.com.`}</Text>
        </ColumnDiv>
        <Button label="Go to my dashboard" to={routeConstants.private.dashboard} />
      </Layout>
    );
  }

  return (
    <Layout>
      {!organizationConnectionsList ? (
        <LoadingHeader />
      ) : (
        <>
          <StyledAuthHeader logo={integration.logo()} data-cy="auth-header-login" title={integration.title} subtitle={integration.subtitle} />
          {renderContent()}
          <ActionButtons>
            <Button label={`Connect ${integrationName} Account`} onClick={handleConnect} isProcessing={isLoadingOauthUrl} />
            <Button label="Go to my dashboard" appearance="link" to={routeConstants.private.dashboard} />
          </ActionButtons>
        </>
      )}
    </Layout>
  );
};
